import {
  CompositeFilterDescriptor,
  DataResult,
  FilterOperator,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMount from '../../../../../common/hooks/useMount';
import { DataLoader } from '../../../../../common/widgets/DataLoader';
import Grid, { GridCellTd } from '../../../../../common/widgets/Grid';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import _ from 'lodash';
import moment from 'moment';
import { HttpResponse } from '../../../../../common/ajax';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../common/global/helpers';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Dropdown from '../../../../../common/widgets/Dropdown';
import ExpandablePanel from '../../../../../common/widgets/ExpandablePanel';
import MonthPicker from '../../../../../common/widgets/MonthPicker';
import { config } from '../../../config';
import { FinalReportItemModel } from '../../../models/FinalReportItem';
import { resources } from '../../../resources/resources';
import { companyService } from '../../../services/company';
import { finalReportsService } from '../../../services/final-reports';
import { ListItem } from '../types';
import {
  ActionsCell,
  DateAddedCell,
  DateCell,
  ReportTypeNameCell,
} from './Components/CustomCells';
import GenerateReport from './Components/GenerateReport';
import DownloadReportDialog from './Dialogs/DownloadReportDialog';
import { FinalReportState } from './FinalReportState';
import { mapToReportGridItem } from './helper';
import {
  CompanyItem,
  FinalReportGridItemModel,
  ReportFileContentTypes,
} from './types';

const initialDataState: State = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [
    {
      field: 'year',
      dir: 'desc',
    },
    {
      field: 'month',
      dir: 'desc',
    },
    { field: 'dateAdded', dir: 'desc' },
  ],
};

const initialDisplaySort: SortDescriptor[] = [{ field: 'date', dir: 'desc' }];

export default function FinalReportPage() {
  const { t: translate } = useTranslation();
  const { isMobile } = useBreakpoints();
  const [state, setState] = useState<FinalReportState>({
    companies: [] as CompanyItem[],
    reports: {
      data: [] as FinalReportGridItemModel[],
      dataState: initialDataState,
      displaySort: initialDisplaySort,
    },
  } as FinalReportState);

  const companyDefaultItem: CompanyItem = {
    id: 0,
    name: translate(resources.admin.employees.filters.companyDefault),
  };

  const fileContentTypes: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.finalReport.allFileContentTypes),
    },
    {
      id: ReportFileContentTypes.FinalReport,
      name: translate(resources.admin.finalReport.grid.finalReport),
    },
    {
      id: ReportFileContentTypes.FinalMonthlyInvoices,
      name: translate(resources.admin.finalReport.grid.finalMonthlyInvoices),
    },
    {
      id: ReportFileContentTypes.AdvanceInvoice,
      name: translate(resources.admin.finalReport.grid.advanceInvoice),
    },
  ];

  const displayToast = (
    hasErrors: boolean,
    errorMessage: string,
    successMessage: string,
  ) => {
    if (hasErrors) {
      showErrorToast(errorMessage, true);
    } else {
      setState(
        produce(draft => {
          draft.needReload = new Date().toUTCString();
        }),
      );
      showSuccessToast(successMessage, true);
    }
  };

  const handleDeleteReport = useCallback(
    async (reportId: number, fileContentTypeId: number) => {
      setState(
        produce(draft => {
          const currentReportIndex =
            draft.reports.data.findIndex(e => e.reportId === reportId) ?? -1;
          draft.reports.data[currentReportIndex].isDeleting = true;
        }),
      );
      let response = null;
      if (fileContentTypeId === ReportFileContentTypes.FinalReport) {
        response = await finalReportsService.deleteReport(reportId);
        if (!response.hasErrors) {
          setState(
            produce(draft => {
              draft.needReload = new Date().toISOString();
            }),
          );
        }
        displayToast(
          response?.hasErrors,
          translate(resources.admin.finalReport.grid.deleteError),
          translate(resources.admin.finalReport.grid.deleteSuccess),
        );
      }
      setState(
        produce(draft => {
          const currentReportIndex =
            draft.reports.data.findIndex(e => e.reportId === reportId) ?? -1;
          draft.reports.data[currentReportIndex].isDeleting = false;
        }),
      );
    },
    [translate],
  );

  const handleDownloadReport = useCallback(
    async (item: FinalReportGridItemModel) => {
      if (!item) {
        return;
      }
      setState(
        produce(draft => {
          const currentReportIndex =
            draft.reports.data.findIndex(e => e.fileCode === item.fileCode) ??
            -1;
          draft.reports.data[currentReportIndex].isDownloading = true;
        }),
      );

      let response: HttpResponse<File>;
      switch (item.fileContentTypeId) {
        case ReportFileContentTypes.FinalReport:
          if (
            (
              await finalReportsService.downloadFinalReportFile(
                item.fileCode,
                `final-report.${item.company}.${moment(
                  new Date(item.dateAdded),
                ).format('DD-MM-YYYY')}`,
              )
            ).hasErrors
          ) {
            showErrorToast(
              translate(resources.admin.finalReport.grid.downloadError),
            );
          }

          break;
        case ReportFileContentTypes.AdvanceInvoice:
          response = await finalReportsService.loadAdvanceInvoiceFile(
            item.fileCode,
          );
          if (!response.hasErrors) {
            setState(
              produce(draft => {
                draft.showDownloadDialog = true;
                draft.file = response.data;
              }),
            );
          } else {
            showErrorToast(
              translate(resources.admin.finalReport.grid.downloadError),
            );
          }
          break;
        case ReportFileContentTypes.FinalMonthlyInvoices:
          if (
            (
              await finalReportsService.downloadInvoicesZipFile(
                item.fileCode,
                `advance-invoice.${item.company}.${moment(
                  new Date(item.dateAdded),
                ).format('DD-MM-YYYY')}`,
              )
            ).hasErrors
          ) {
            showErrorToast(
              translate(resources.admin.finalReport.grid.downloadError),
            );
          }
          break;
        default:
          break;
      }
      setState(
        produce(draft => {
          const currentReportIndex =
            draft.reports.data.findIndex(e => e.fileCode === item.fileCode) ??
            -1;
          draft.reports.data[currentReportIndex].isDownloading = false;
        }),
      );
    },
    [translate],
  );

  const loadCompanies = async (
    abortController: AbortController | undefined,
  ) => {
    const result = await companyService.getCompaniesByClient(
      '?$select=CompanyId, Name',
      {
        signal: abortController?.signal,
      },
    );

    if (!result.hasErrors) {
      setState(
        produce(draft => {
          draft.companies = result.data.map(c => {
            return { id: c.companyId, name: c.name };
          }) as CompanyItem[];
        }),
      );

      setState(
        produce(draft => {
          draft.isOneCompany = result.data.length === 1;
        }),
      );
    }
  };

  useMount(async abortController => {
    await loadCompanies(abortController);
  });

  const [fileContentTypeFilter, setFileContentTypeFilter] =
    useState<ListItem | null>(fileContentTypes[0]);
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [companyFilter, setCompanyFilter] =
    useState<ListItem>(companyDefaultItem);

  const onCompanyFilterChanged = (e: DropDownListChangeEvent) => {
    const company = e.value as ListItem;
    setCompanyFilter(company);
  };

  const onFileContentTypeFilterChanged = (e: DropDownListChangeEvent) => {
    const fileContentType = e.value as ListItem;
    setFileContentTypeFilter(fileContentType);
  };

  const onDateFilterChanged = (e: any) => {
    setDateFilter(e.value);
  };

  const onCompanyFilterClear = () => {
    setCompanyFilter(companyDefaultItem);
  };

  const onDateFilterClear = () => {
    setDateFilter(null);
  };

  const onFileContentTypeFilterClear = () => {
    setFileContentTypeFilter(fileContentTypes[0]);
  };

  const getFilter = useCallback((): CompositeFilterDescriptor => {
    const filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
    if (companyFilter.id !== 0)
      filter.filters.push({
        field: 'companyId',
        operator: FilterOperator.EqualTo,
        value: companyFilter.id,
      });

    if (fileContentTypeFilter && fileContentTypeFilter.id !== 0)
      filter.filters.push({
        field: 'fileContentTypeId',
        operator: FilterOperator.EqualTo,
        value: fileContentTypeFilter?.id,
      });

    if (dateFilter) {
      filter.filters.push(
        {
          field: 'month',
          operator: FilterOperator.EqualTo,
          value: dateFilter.getMonth() + 1,
        },
        {
          field: 'year',
          operator: FilterOperator.EqualTo,
          value: dateFilter.getFullYear(),
        },
      );
    }

    return filter;
  }, [companyFilter.id, fileContentTypeFilter, dateFilter]);

  const setFilter = useCallback(() => {
    const filter = getFilter();
    setState(
      produce(d => {
        d.reports.dataState.skip = 0;
        d.reports.dataState.filter = filter;
      }),
    );
  }, [getFilter]);

  useEffect(() => {
    setFilter();
  }, [companyFilter, setFilter]);

  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    const processingReportsIds = state.reports.data
      .filter(e => e.canDownload === false)
      .map(e => e.reportId);

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (processingReportsIds.length > 0) {
      intervalRef.current = setInterval(async () => {
        try {
          const response =
            await finalReportsService.getReportStatusForRefresh(
              processingReportsIds,
            );

          if (response.hasErrors) {
            showErrorToast(translate(resources.admin.finalReport.errorMessage));
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else if (!response.hasErrors && response.data) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            showSuccessToast(translate(resources.admin.finalReport.success));

            // Reload reports
            setState(
              produce(draft => {
                draft.needReload = new Date().toISOString();
              }),
            );
          }
        } catch (error) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          console.error(
            translate(resources.admin.finalReport.errorMessage),
            error,
          );
        }
      }, 15000) as unknown as number;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state.reports.data, translate]);

  const onDataReceived = (result: DataResult) => {
    setState(
      produce(draft => {
        draft.reports.data = result.data.map((e: FinalReportItemModel) =>
          mapToReportGridItem(e, handleDeleteReport, handleDownloadReport),
        );
        draft.reports.total = result.total;
      }),
    );
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(draft => {
        const previousSort = draft.reports.dataState.sort;
        const previousSkip = draft.reports.dataState.skip;
        const previousTake = draft.reports.dataState.take;

        let newSort = e.dataState.sort;
        let displaySort = e.dataState.sort || [];

        // Check if sorting by 'date' field and replace with year/month
        if (newSort && newSort.length > 0 && newSort[0].field === 'date') {
          const dateDir = newSort[0].dir || 'desc';
          displaySort = [{ field: 'date', dir: dateDir }]; // Keep 'date' for display
          newSort = [
            { field: 'year', dir: dateDir },
            { field: 'month', dir: dateDir },
          ];
        }

        const sortChanged =
          JSON.stringify(previousSort) !== JSON.stringify(newSort);

        const pageChanged =
          previousSkip !== e.dataState.skip ||
          previousTake !== e.dataState.take;

        draft.reports.dataState = {
          ...e.dataState,
          sort: newSort,
          skip: sortChanged && !pageChanged ? 0 : e.dataState.skip,
        };

        draft.reports.displaySort = displaySort;
      }),
    );
  };

  return (
    <div className="panel padding-xl final-reports-section">
      <div className="page-actions">
        <h1>{translate(resources.admin.finalReport.title)}</h1>
        <div className="page-action admin-actions">
          <GenerateReport state={state} setState={setState} />
        </div>
      </div>

      {/* Final Reports search*/}
      {isMobile ? (
        <div className="employee-search-mobile">
          <ExpandablePanel
            expandOn={'header'}
            arrow={false}
            title={<div>{translate(resources.admin.advancedSearchLabel)}</div>}
          >
            <div className="admin-employees-adv-search">
              {!state.isOneCompany && (
                <div className="esi-c">
                  <Dropdown
                    value={companyFilter}
                    defaultValue={companyDefaultItem}
                    data={_.orderBy(
                      [...state.companies, companyDefaultItem],
                      'id',
                      'asc',
                    )}
                    dataItemKey="id"
                    textField="name"
                    onChange={onCompanyFilterChanged}
                    onClear={
                      companyFilter?.id === companyDefaultItem.id
                        ? undefined
                        : onCompanyFilterClear
                    }
                  />
                </div>
              )}
              <div className="esi-c">
                <Dropdown
                  value={fileContentTypeFilter}
                  defaultValue={fileContentTypes[0]}
                  data={fileContentTypes}
                  dataItemKey="id"
                  textField="name"
                  onChange={onFileContentTypeFilterChanged}
                  onClear={
                    fileContentTypeFilter?.id === 0
                      ? undefined
                      : onFileContentTypeFilterClear
                  }
                />
              </div>
              <div className="esi-c">
                <MonthPicker
                  value={dateFilter}
                  placeholder={translate(
                    resources.admin.finalReport.periodFilter,
                  )}
                  onChange={onDateFilterChanged}
                  onClear={onDateFilterClear}
                ></MonthPicker>
              </div>
            </div>
          </ExpandablePanel>
        </div>
      ) : (
        <div className="employee-search final-reports-filter">
          <div>
            <div>
              <div className="final-reports-filter-left">
                {!state.isOneCompany && (
                  <div className=" employee-search-column final-reports-filter-left-item">
                    <Dropdown
                      value={companyFilter}
                      defaultValue={companyDefaultItem}
                      data={_.orderBy(
                        [...state.companies, companyDefaultItem],
                        'id',
                        'asc',
                      )}
                      dataItemKey="id"
                      textField="name"
                      onChange={onCompanyFilterChanged}
                      onClear={
                        companyFilter?.id === companyDefaultItem.id
                          ? undefined
                          : onCompanyFilterClear
                      }
                    />
                  </div>
                )}
                <div className="employee-search-column final-reports-filter-left-item">
                  <Dropdown
                    value={fileContentTypeFilter}
                    defaultValue={fileContentTypes[0]}
                    data={fileContentTypes}
                    dataItemKey="id"
                    textField="name"
                    onChange={onFileContentTypeFilterChanged}
                    onClear={
                      fileContentTypeFilter?.id === 0
                        ? undefined
                        : onFileContentTypeFilterClear
                    }
                  />
                </div>
              </div>
              <div className="employee-search-column final-reports-filter-right">
                <MonthPicker
                  value={dateFilter}
                  placeholder={translate(
                    resources.admin.finalReport.periodFilter,
                  )}
                  onChange={onDateFilterChanged}
                  onClear={onDateFilterClear}
                ></MonthPicker>
              </div>
            </div>
          </div>
        </div>
      )}

      <DataLoader
        getData={finalReportsService.getFinalReports}
        dataState={state.reports.dataState}
        onDataReceived={onDataReceived}
        reloadTrigger={state.needReload}
      />
      <div>
        <Loader loading={!state.reports.data} loader={<LoadingSpinner />}>
          <div className="admin-grid final-reports-grid">
            <Grid
              data={state.reports.data}
              pageable={{
                pageSizes: config.grid.pageSizes,
                pageSizeValue: state.reports.dataState.take,
                info: false,
                previousNext: true,
              }}
              {...state.reports.dataState}
              onDataStateChange={e => onDataStateChange(e)}
              total={state.reports.total}
              sort={state.reports.displaySort ?? []}
              sortable={{ allowUnsort: false, mode: 'single' }}
              columns={[
                {
                  title: translate(
                    resources.admin.finalReport.grid.finalReport,
                  ),
                  field: 'fileContentTypeId',
                  sortable: false,
                  cell: props => reportTypeNameCell(props, state.isOneCompany),
                },
                {
                  title: translate(
                    resources.admin.finalReport.grid.generatedBy,
                  ),
                  field: 'generatedBy',
                  sortable: false,
                  cell: generatedByCell,
                },
                {
                  title: translate(resources.admin.finalReport.grid.date),
                  field: 'date',
                  sortable: true,
                  cell: DateCell,
                },
                {
                  title: translate(resources.admin.finalReport.grid.dateAdded),
                  field: 'dateAdded',
                  sortable: true,
                  cell: DateAddedCell,
                },
                {
                  cell: ActionsCell,
                  headerClassName: 'grid-actions-cell',
                },
              ]}
            />
          </div>
        </Loader>
      </div>
      {state.showDownloadDialog && (
        <DownloadReportDialog
          file={state.file}
          setDialog={setState}
        ></DownloadReportDialog>
      )}
    </div>
  );
}

const generatedByCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.generatedBy}</GridCellTd>
);

const reportTypeNameCell = (props: GridCellProps, isOneCompany: boolean) => (
  <ReportTypeNameCell {...props} isOneCompany={isOneCompany} />
);
